import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { get, post } from '../lib/api';
import TopBar from '../components/layout/TopBar';
import SectionCard from '../components/widgets/SectionCard';
import KpiCard from '../components/widgets/KpiCard';
import { fmtBRL, fmtPct } from '../lib/format';
import { toast } from 'sonner';
import { Target, TrendingUp, TrendingDown } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';

export default function MetaVsRealizado() {
  const { competence, companies } = useApp();
  const [goals, setGoals] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [edit, setEdit] = useState({}); // {company_id: {target_revenue, target_expenses, target_profit}}

  const load = async () => {
    const [g, k] = await Promise.all([
      get('/goals', { competence }),
      get('/dashboard/kpis', { competence }),
    ]);
    setGoals(g); setKpis(k);
    const m = {};
    g.forEach((x) => { m[x.company_id || 'consolidated'] = x; });
    setEdit(m);
  };
  useEffect(() => { load(); }, [competence]);

  const saveGoal = async (company_id) => {
    const cur = edit[company_id] || {};
    await post('/goals', {
      company_id,
      competence,
      target_revenue: parseFloat(String(cur.target_revenue || 0).replace(',', '.')) || 0,
      target_expenses: parseFloat(String(cur.target_expenses || 0).replace(',', '.')) || 0,
      target_profit: parseFloat(String(cur.target_profit || 0).replace(',', '.')) || 0,
    });
    toast.success('Meta salva');
    load();
  };

  const getReal = (cid) => {
    if (!kpis) return { receita: 0, despesas: 0, lucro: 0 };
    const c = kpis.by_company.find((x) => x.company_id === cid);
    if (!c) return { receita: 0, despesas: 0, lucro: 0 };
    return { receita: c.receita, despesas: c.custos + c.despesas, lucro: c.lucro_liquido };
  };

  const consolReceita = kpis?.consolidated?.receita || 0;
  const consolDespesas = (kpis?.consolidated?.custos || 0) + (kpis?.consolidated?.despesas || 0);
  const consolLucro = kpis?.consolidated?.lucro_liquido || 0;

  const totalTargetReceita = goals.reduce((s, g) => s + (g.target_revenue || 0), 0);
  const totalTargetLucro = goals.reduce((s, g) => s + (g.target_profit || 0), 0);

  return (
    <div className="fade-in">
      <TopBar title="Meta vs Realizado" subtitle="Acompanhe o desempenho contra as metas definidas" />
      <div className="px-8 py-7 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KpiCard
            label="Meta Receita Total"
            value={totalTargetReceita}
            accent="#D4AF37"
            icon={Target}
            testid="kpi-target-receita"
            deltaLabel={`Realizado ${fmtPct(totalTargetReceita ? (consolReceita / totalTargetReceita) * 100 : 0)}`}
          />
          <KpiCard
            label="Receita Realizada"
            value={consolReceita}
            accent="#10B981"
            icon={TrendingUp}
            testid="kpi-real-receita"
            highlight
          />
          <KpiCard
            label="Meta Lucro Total"
            value={totalTargetLucro}
            accent="#3B82F6"
            icon={TrendingDown}
            testid="kpi-target-lucro"
            deltaLabel={`Realizado ${fmtPct(totalTargetLucro ? (consolLucro / totalTargetLucro) * 100 : 0)}`}
          />
        </div>

        <SectionCard
          title={`Comparativo por Empresa - Competência ${competence}`}
          padded={false}
          testid="meta-table"
        >
          <div className="overflow-x-auto">
            <table className="w-full ceap-table">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th className="text-right">Meta Receita</th>
                  <th className="text-right">Realizado</th>
                  <th className="text-right">Atingimento</th>
                  <th className="text-right">Meta Despesas</th>
                  <th className="text-right">Realizado</th>
                  <th className="text-right">Meta Lucro</th>
                  <th className="text-right">Realizado</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((co) => {
                  const real = getReal(co.id);
                  const cur = edit[co.id] || {};
                  const atRev = cur.target_revenue ? (real.receita / cur.target_revenue) * 100 : 0;
                  return (
                    <tr key={co.id} data-testid={`meta-row-${co.id}`}>
                      <td>
                        <div className="flex items-center gap-2">
                          <span className="w-1.5 h-7 rounded-sm" style={{ background: co.color }} />
                          <span className="text-zinc-100">{co.name}</span>
                        </div>
                      </td>
                      <td className="text-right">
                        <Input
                          value={cur.target_revenue || ''}
                          onChange={(e) => setEdit({ ...edit, [co.id]: { ...cur, target_revenue: e.target.value } })}
                          className="bg-zinc-900 border-zinc-800 text-right font-mono w-32 inline-block"
                          placeholder="0"
                          data-testid={`input-target-revenue-${co.id}`}
                        />
                      </td>
                      <td className="text-right font-mono gold-text">{fmtBRL(real.receita)}</td>
                      <td className="text-right">
                        <span className={`font-mono ${atRev >= 100 ? 'emerald-text' : (atRev >= 70 ? 'text-amber-400' : 'red-text')}`}>
                          {fmtPct(atRev)}
                        </span>
                      </td>
                      <td className="text-right">
                        <Input
                          value={cur.target_expenses || ''}
                          onChange={(e) => setEdit({ ...edit, [co.id]: { ...cur, target_expenses: e.target.value } })}
                          className="bg-zinc-900 border-zinc-800 text-right font-mono w-32 inline-block"
                          placeholder="0"
                          data-testid={`input-target-expenses-${co.id}`}
                        />
                      </td>
                      <td className="text-right font-mono red-text">{fmtBRL(real.despesas)}</td>
                      <td className="text-right">
                        <Input
                          value={cur.target_profit || ''}
                          onChange={(e) => setEdit({ ...edit, [co.id]: { ...cur, target_profit: e.target.value } })}
                          className="bg-zinc-900 border-zinc-800 text-right font-mono w-32 inline-block"
                          placeholder="0"
                          data-testid={`input-target-profit-${co.id}`}
                        />
                      </td>
                      <td className={`text-right font-mono ${real.lucro >= 0 ? 'emerald-text' : 'red-text'}`}>{fmtBRL(real.lucro)}</td>
                      <td className="text-right">
                        <Button size="sm" onClick={() => saveGoal(co.id)} className="btn-gold text-xs px-3 py-1 h-7" data-testid={`btn-save-goal-${co.id}`}>
                          Salvar
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
